import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import type { z } from 'zod';
import { backlogItemsDataSetFetchOptions } from '@/lib/queries/adaptive-planner/useFetchBacklogItemsDataSet';
import { getPlanQueryOptions } from '@/lib/queries/adaptive-planner/useGetPlanQuery';
import { usePlanPermissionOptions } from '@/lib/queries/adaptive-planner/usePlanPermission';
import { itemsDataSetQueryOptions } from '@/lib/queries/common/useGetItemsDataSetQuery';
import i18n from '@/lib/translations/i18n';
import type { LoaderData } from '@/lib/types/common';
import { backlogItemsFilterSearchSchema } from '@/lib/zod-schemas/backlogItemsFilterSearchSchema';
import { descendantItemFilterSearchSchema } from '@/lib/zod-schemas/descendantItemFilterSearchSchema';
import { itemFilterSearchSchema } from '@/lib/zod-schemas/itemFilterSearchSchema';
import { itemPanelSearchSchema } from '@/lib/zod-schemas/itemPanelSearchSchema';

const backlogSearchSchema = itemFilterSearchSchema
  .and(itemPanelSearchSchema)
  .and(descendantItemFilterSearchSchema)
  .and(backlogItemsFilterSearchSchema);

export type BacklogRouteSearch = z.infer<typeof backlogSearchSchema>;

export const Route = createFileRoute('/(app)/org/$org/adaptive-planner/plan/$planId/backlog')({
  validateSearch: zodValidator(backlogSearchSchema),
  loader: async ({ context: { queryClient }, params: { planId } }): Promise<LoaderData> => {
    const planNum = Number(planId);
    const plan = await queryClient.ensureQueryData(getPlanQueryOptions({ planId: planNum }));
    const crumb = `${plan.name} ${i18n.t('adaptivePlanner.scope')}`;
    await queryClient.ensureQueryData(
      itemsDataSetQueryOptions({
        filter: {
          'descendant.statusSchedulingType': {
            value: ['SCHEDULED', 'UNSCHEDULED'],
            operator: 'in',
          },
        },
      }),
    );
    await queryClient.ensureQueryData(usePlanPermissionOptions({ planId: planNum }));
    await queryClient.ensureQueryData(
      backlogItemsDataSetFetchOptions({
        planId: planNum,
      }),
    );
    return {
      crumb,
    };
  },
});
